import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  BulkCreateVariantsOptions,
  BulkCreateVariantsResponse,
  BulkDeleteVariantsResponse,
  BulkUpdateVariantsOptions,
  BulkUpdateVariantsResponse,
  CountVariantsApplicationErrors,
  CountVariantsOptions,
  CountVariantsResponse,
  ItemVariantCreatedEnvelope,
  ItemVariantDeletedEnvelope,
  ItemVariantUpdatedEnvelope,
  ListVariantsOptions,
  ListVariantsResponse,
  MaskedVariant,
  QueryVariantsResponse,
  UpdateVariant,
  Variant,
  VariantQuery,
  VariantsQueryBuilder,
  bulkCreateVariants as universalBulkCreateVariants,
  bulkDeleteVariants as universalBulkDeleteVariants,
  bulkUpdateVariants as universalBulkUpdateVariants,
  countVariants as universalCountVariants,
  createVariant as universalCreateVariant,
  deleteVariant as universalDeleteVariant,
  getVariant as universalGetVariant,
  listVariants as universalListVariants,
  queryVariants as universalQueryVariants,
  typedQueryVariants as universalTypedQueryVariants,
  updateVariant as universalUpdateVariant,
} from './restaurants-menus-v1-item-variant-item-variants.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/restaurants' };

export function createVariant(httpClient: HttpClient): CreateVariantSignature {
  return (variant: NonNullablePaths<Variant, `name`, 2>) =>
    universalCreateVariant(
      variant,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateVariantSignature {
  /**
   * > **Note:** The Item Variants API only works with the Wix Restaurants Menus app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
   *
   * Creates an item variant.
   *
   * To create multiple item variants at once, use [Bulk Create Variants](https://dev.wix.com/docs/rest/business-solutions/restaurants/menus/item-variant/bulk-create-variants).
   * @param - Item variant info.
   * @returns Item variant.
   */
  (variant: NonNullablePaths<Variant, `name`, 2>): Promise<
    NonNullablePaths<Variant, `businessLocationIds`, 2>
  >;
}

export function getVariant(httpClient: HttpClient): GetVariantSignature {
  return (variantId: string) =>
    universalGetVariant(
      variantId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetVariantSignature {
  /**
   * > **Note:** The Item Variants API only works with the Wix Restaurants Menus app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
   *
   * Retrieves an item variant by ID.
   * @param - ID of the item variant to retrieve.
   * @returns Item variant.
   */
  (variantId: string): Promise<
    NonNullablePaths<Variant, `businessLocationIds`, 2>
  >;
}

export function listVariants(httpClient: HttpClient): ListVariantsSignature {
  return (options?: ListVariantsOptions) =>
    universalListVariants(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListVariantsSignature {
  /**
   * > **Note:** The Item Variants API only works with the Wix Restaurants Menus app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
   *
   * Retrieves a list of up to 500 item variants.
   */
  (options?: ListVariantsOptions): Promise<
    NonNullablePaths<ListVariantsResponse, `variants`, 2>
  >;
}

export function updateVariant(httpClient: HttpClient): UpdateVariantSignature {
  return (
    _id: string,
    variant: NonNullablePaths<UpdateVariant, `revision`, 2>
  ) =>
    universalUpdateVariant(
      _id,
      variant,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateVariantSignature {
  /**
   * > **Note:** The Item Variants API only works with the Wix Restaurants Menus app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
   *
   * Updates an item variant.
   *
   * To update multiple item variants at once, use [Bulk Update Variants](https://dev.wix.com/docs/rest/business-solutions/restaurants/menus/item-variant/bulk-update-variants).
   *
   * Each time an item variant is updated, its revision increments by 1. The existing revision must be included when updating the variant. This ensures you're working with the latest variant information, and it prevents unintended overwrites.
   * @param - Item variant ID.
   * @returns Updated item variant.
   */
  (
    _id: string,
    variant: NonNullablePaths<UpdateVariant, `revision`, 2>
  ): Promise<NonNullablePaths<Variant, `businessLocationIds`, 2>>;
}

export function bulkCreateVariants(
  httpClient: HttpClient
): BulkCreateVariantsSignature {
  return (variants: Variant[], options?: BulkCreateVariantsOptions) =>
    universalBulkCreateVariants(
      variants,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkCreateVariantsSignature {
  /**
   * > **Note:** The Item Variants API only works with the Wix Restaurants Menus app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
   *
   * Creates multiple item variants.
   * @param - Item variants details.
   */
  (variants: Variant[], options?: BulkCreateVariantsOptions): Promise<
    NonNullablePaths<
      BulkCreateVariantsResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

export function bulkUpdateVariants(
  httpClient: HttpClient
): BulkUpdateVariantsSignature {
  return (variants: MaskedVariant[], options?: BulkUpdateVariantsOptions) =>
    universalBulkUpdateVariants(
      variants,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdateVariantsSignature {
  /**
   * > **Note:** The Item Variants API only works with the Wix Restaurants Menus app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
   *
   * Updates multiple item variants.
   *
   * Each time an item variant is updated, its revision increments by 1. The existing revision must be included when updating variant. This ensures you're working with the latest item information, and it prevents unintended overwrites.
   *
   * Up to 100 item variants can be returned per request.
   * @param - Item variants to be updated.
   */
  (variants: MaskedVariant[], options?: BulkUpdateVariantsOptions): Promise<
    NonNullablePaths<
      BulkUpdateVariantsResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

export function deleteVariant(httpClient: HttpClient): DeleteVariantSignature {
  return (variantId: string) =>
    universalDeleteVariant(
      variantId,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteVariantSignature {
  /**
   * > **Note:** The Item Variants API only works with the Wix Restaurants Menus app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
   *
   * Permanently deletes an item variant.
   * @param - Item variant ID.
   */
  (variantId: string): Promise<void>;
}

export function queryVariants(httpClient: HttpClient): QueryVariantsSignature {
  return () =>
    universalQueryVariants(
      // @ts-ignore
      { httpClient }
    );
}

interface QueryVariantsSignature {
  /**
   * Creates a query to retrieve a list of item variants.
   *
   * The `queryVariants()` function builds a query to retrieve a list of item variants and returns a `VariantsQueryBuilder` object.
   *
   * The returned object contains the query definition, which is used to run the query using the [`find()`](/item-variants/variants-query-builder/find) function.
   *
   * You can refine the query by chaining `VariantsQueryBuilder` functions onto the query. `VariantsQueryBuilder` functions enable you to filter, sort, and control the results that `queryVariants()` returns.
   *
   * `queryVariants()` runs with the following `VariantsQueryBuilder` defaults, which you can override:
   *
   * * [`limit(500)`](/item-variants/variants-query-builder/limit)
   *
   * The following `VariantsQueryBuilder` functions are supported for `queryVariants()`. For a full description of the item variant object, see the object returned for the [`items`](/item-variants/variants-query-result/items) property in `VariantsQueryResult`.
   */
  (): VariantsQueryBuilder;
}

export function typedQueryVariants(
  httpClient: HttpClient
): TypedQueryVariantsSignature {
  return (query: VariantQuery) =>
    universalTypedQueryVariants(
      query,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryVariantsSignature {
  /** */
  (query: VariantQuery): Promise<
    NonNullablePaths<QueryVariantsResponse, `variants`, 2>
  >;
}

export function countVariants(httpClient: HttpClient): CountVariantsSignature {
  return (options?: CountVariantsOptions) =>
    universalCountVariants(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface CountVariantsSignature {
  /**
   * > **Note:** The Item API only works with the Wix Restaurants Menus app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
   *
   * Retrieves the number of item variants that match a specified filter.
   *
   * If a filter isn't passed in the request, the endpoint returns the count of all item variants.
   */
  (options?: CountVariantsOptions): Promise<
    NonNullablePaths<CountVariantsResponse, `count`, 2> & {
      __applicationErrorsType?: CountVariantsApplicationErrors;
    }
  >;
}

export function bulkDeleteVariants(
  httpClient: HttpClient
): BulkDeleteVariantsSignature {
  return (ids: string[]) =>
    universalBulkDeleteVariants(
      ids,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkDeleteVariantsSignature {
  /**
   * > **Note:** The Item Variants API only works with the Wix Restaurants Menus app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
   *
   * Deletes multiple item variants at once.
   * @param - Item variant IDs.
   */
  (ids: string[]): Promise<
    NonNullablePaths<
      BulkDeleteVariantsResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

export const onItemVariantCreated = EventDefinition(
  'wix.restaurants.menus.v1.item_variant_created',
  true,
  (event: ItemVariantCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'entity.externalReferenceInfo.lastSyncDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<ItemVariantCreatedEnvelope>();
export const onItemVariantDeleted = EventDefinition(
  'wix.restaurants.menus.v1.item_variant_deleted',
  true,
  (event: ItemVariantDeletedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'undefined.createdDate' },
            { path: 'undefined.updatedDate' },
            { path: 'undefined.externalReferenceInfo.lastSyncDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<ItemVariantDeletedEnvelope>();
export const onItemVariantUpdated = EventDefinition(
  'wix.restaurants.menus.v1.item_variant_updated',
  true,
  (event: ItemVariantUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'entity.externalReferenceInfo.lastSyncDate' },
            { path: 'metadata.eventTime' },
            { path: 'modifiedFields.createdDate' },
            { path: 'modifiedFields.updatedDate' },
            { path: 'modifiedFields.externalReferenceInfo.lastSyncDate' },
          ],
        },
      ])
    )
)<ItemVariantUpdatedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  App,
  ApplicationError,
  BaseEventMetadata,
  BulkActionMetadata,
  BulkCreateVariantsOptions,
  BulkCreateVariantsRequest,
  BulkCreateVariantsResponse,
  BulkDeleteVariantsRequest,
  BulkDeleteVariantsResponse,
  BulkDeleteVariantsResult,
  BulkItemResult,
  BulkUpdateAttachedToOrphanItemRequest,
  BulkUpdateAttachedToOrphanItemResponse,
  BulkUpdateBusinessLocationIdsPerEntityRequest,
  BulkUpdateBusinessLocationIdsPerEntityResponse,
  BulkUpdateBusinessLocationIdsPerEntityResponseBulkItemResult,
  BulkUpdateBusinessLocationIdsRequest,
  BulkUpdateBusinessLocationIdsResponse,
  BulkUpdateBusinessLocationIdsResponseBulkItemResult,
  BulkUpdateVariantsOptions,
  BulkUpdateVariantsRequest,
  BulkUpdateVariantsResponse,
  BulkVariantResult,
  CloneVariantsRequest,
  CloneVariantsResponse,
  CountVariantsOptions,
  CountVariantsRequest,
  CountVariantsResponse,
  CreateVariantRequest,
  CreateVariantResponse,
  CursorPaging,
  CursorPagingMetadata,
  CursorQuery,
  CursorQueryPagingMethodOneOf,
  Cursors,
  CustomTag,
  DeleteVariantRequest,
  DeleteVariantResponse,
  DomainEvent,
  DomainEventBodyOneOf,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventMetadata,
  ExtendedFields,
  ExternalReferenceInfo,
  File,
  GetVariantRequest,
  GetVariantResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  InvalidateCache,
  InvalidateCacheGetByOneOf,
  ItemMetadata,
  ItemVariantCreatedEnvelope,
  ItemVariantDeletedEnvelope,
  ItemVariantUpdatedEnvelope,
  ListVariantsOptions,
  ListVariantsRequest,
  ListVariantsResponse,
  MaskedVariant,
  MessageEnvelope,
  Page,
  Pages,
  QueryVariantsRequest,
  QueryVariantsResponse,
  RestoreInfo,
  SortOrder,
  Sorting,
  SyncContentToMultilingualRequest,
  SyncContentToMultilingualResponse,
  URI,
  URIs,
  UpdateVariant,
  UpdateVariantRequest,
  UpdateVariantResponse,
  Variant,
  VariantIdAttachedToOrphanItem,
  VariantIdLocationIds,
  VariantQuerySpec,
  VariantsQueryBuilder,
  VariantsQueryResult,
  WebhookIdentityType,
  utils,
} from './restaurants-menus-v1-item-variant-item-variants.universal.js';
